import { AnalyticsService, AnalyticsTrackingEvent, tryGetVisitorId } from '../../analytics';

/**
 * Emit `webApp:organic:openRetailerProductPage` when a shopper leaves the concierge
 * for a retailer listing (the "View" button in the product detail panel).
 *
 * WHY THIS EXISTS — external order attribution
 * --------------------------------------------
 * External orders are inferred by the analytics spark job, not by us. Its
 * eligibility gate (`externalOrderEligibleUDF`) is narrow, and an event that misses
 * any part of it is silently ignored rather than rejected, so each property below
 * is load-bearing:
 *
 *  - The EVENT NAME must be exactly this. Two separate allowlists filter on it: the
 *    Athena view `mixpanel_prod_page_views_and_clicks_v1` (which drops every other
 *    event before the job sees it) and `Events.BuyboxClickEventTypes` (which decides
 *    `interactionType = buyboxClick`; anything else becomes a mere `view`).
 *  - `retailerId` must be < 1000. Marketplace retailers only — DTC pseudo-retailers
 *    are deliberately excluded from external orders.
 *  - `retailerSku` AND `sku`/`stacklineSku` must both be present.
 *  - `availabilityStatus` must equal the string `'InStock'`.
 *  - `v_id` carries the visitor id. NOT `visitorId` — the flattened Athena view
 *    reads `properties.v_id`, matching how the storefront and checkout app emit it.
 *    Sourced via `tryGetVisitorId` (the analytics module) rather than Auth's cookie
 *    reader: Auth pulls in `@aws-amplify/*` and runs a module-level side effect, which
 *    has no business in the product panel's import graph. This is also the exact
 *    source the storefront's mixpanel adapter uses for `v_id`.
 *  - `ipAddress` must be non-null; `AnalyticsService.track` already attaches it from
 *    the fingerprint result, so it is not set here.
 *  - `$os` gives the job `appType = web`; the Mixpanel SDK sets it automatically.
 *
 * `pageType` is deliberately NOT sent: this event name is in the job's
 * `ProductOnlyEventType`, so it already resolves to a Product page and therefore
 * `engagementTag = catalog`.
 *
 * Eligible clicks are then sampled by a deterministic hash (74.9% desktop / 83.3%
 * mobile), so a correct event does not always produce an order.
 */
export interface RetailerProductPageOpenParams {
  /** The Brandclub sku of the listing being opened. */
  stacklineSku: string;
  /** The retailer's own listing id. Without it the click cannot become an order. */
  retailerSku?: string;
  retailerId?: number;
  retailerName?: string;
  retailPrice?: number;
  retailerProductPageUrl?: string;
  /** `undefined` when unknown — `availabilityStatus` is then omitted rather than
   *  guessed, since guessing `InStock` would fabricate orders. */
  isAvailable?: boolean;
  /** Ties the retailer exit back to the conversation for concierge reporting. Not
   *  read by the external-order job. */
  conversationId?: string | null;
  appId?: string;
}

/**
 * Fire-and-forget. Resolving the visitor id is async, which is safe here ONLY because
 * both call sites are `target="_blank"` anchors: the tab that emits stays alive, so
 * nothing is lost to an unload race. A same-tab retailer exit would need the id
 * resolved up front (or `sendImmediately`).
 */
export function trackRetailerProductPageOpen(params: RetailerProductPageOpenParams): void {
  // Swallow everything. This runs from an anchor's onClick, so a throw would surface
  // as a React event-handler error and an unhandled rejection would trip error
  // overlays / reporters — for a fire-and-forget analytics call that must never
  // affect the shopper reaching the retailer.
  try {
    emit(params).catch(() => {});
  } catch {
    /* no-op */
  }
}

async function emit(params: RetailerProductPageOpenParams): Promise<void> {
  const visitorId = await tryGetVisitorId();

  await AnalyticsService.track(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
    // `organicType` is what the flattened view uses to rebuild the full event name
    // for the historical rows that were emitted as a bare `webApp:organic:`.
    organicType: 'openRetailerProductPage',
    // Both spellings: the view coalesces `properties.sku` then `properties.stacklineSku`.
    sku: params.stacklineSku,
    stacklineSku: params.stacklineSku,
    ...(params.retailerSku ? { retailerSku: params.retailerSku } : {}),
    ...(params.retailerId != null ? { retailerId: params.retailerId } : {}),
    ...(params.retailerName ? { retailerName: params.retailerName } : {}),
    ...(params.retailPrice != null ? { retailPrice: params.retailPrice } : {}),
    ...(params.retailerProductPageUrl ? { retailerProductPageUrl: params.retailerProductPageUrl } : {}),
    ...(params.isAvailable === undefined ? {} : { availabilityStatus: params.isAvailable ? 'InStock' : 'OutOfStock' }),
    ...(visitorId ? { v_id: visitorId } : {}),
    ...(params.conversationId ? { conversationId: params.conversationId } : {}),
    ...(params.appId ? { appId: params.appId } : {}),
    source: 'concierge_product_detail_view',
  }).catch(() => {
    // Tracking must never break the shopper leaving for the retailer.
  });
}
